import { useState, useEffect } from 'react'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth'
import { apiPost, apiFetch } from '@/lib/api'
import { AlertResponseSchema } from '@ekorank/shared'
import { LoadingSpinner } from '@/components/LoadingSpinner'

const AlertsListSchema = z.object({
  ok: z.literal(true),
  data: z.array(AlertResponseSchema),
})

const CreateAlertResponse = z.object({
  ok: z.literal(true),
  data: AlertResponseSchema,
})

const CheckoutUrlSchema = z.object({
  ok: z.literal(true),
  data: z.object({ url: z.string() }),
})

const SubscriptionSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    tier: z.enum(['free', 'pro', 'agency']),
    subscriptionStatus: z.enum(['active', 'canceled', 'past_due']).nullable(),
    billingPeriodEnd: z.number().nullable(),
    token: z.string().optional(),
    user: z
      .object({
        id: z.string(),
        email: z.string(),
        tier: z.enum(['free', 'pro', 'agency']),
        trialExpiresAt: z.number().nullable(),
      })
      .optional(),
  }),
})

const TIER_LABELS: Record<string, string> = {
  free: 'Free — 10 credits/mo · 1 brand · weekly checks',
  pro: 'Pro — 1,000 credits/mo · 5 brands · daily checks · $14.99/mo',
  agency: 'Agency — 5,000 credits/mo · unlimited brands · custom intervals · webhooks',
}

const CreditsSchema = z.object({
  ok: z.literal(true),
  data: z.object({ creditsBalance: z.number(), monthlyCredits: z.number() }),
})

export default function Settings() {
  const user = useAuthStore((s) => s.user)
  const setAuth = useAuthStore((s) => s.setAuth)

  const [alerts, setAlerts] = useState<z.infer<typeof AlertResponseSchema>[]>([])
  const [loading, setLoading] = useState(true)
  const [alertType, setAlertType] = useState<'email' | 'webhook'>('email')
  const [destination, setDestination] = useState('')
  const [saving, setSaving] = useState(false)
  const [billingLoading, setBillingLoading] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)
  const [credits, setCredits] = useState<{ balance: number; monthly: number } | null>(null)

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    if (params.get('upgraded') === '1') {
      window.history.replaceState({}, '', '/settings')
      void apiFetch('/api/billing/subscription', SubscriptionSchema).then((r) => {
        if (r.ok && r.data.data.token && r.data.data.user) {
          setAuth(r.data.data.user, r.data.data.token)
          setFeedback({ type: 'success', msg: 'You are now on the Pro plan!' })
        } else {
          // Webhook may not have fired yet
          setFeedback({
            type: 'success',
            msg: 'Payment received. Your plan will update shortly — refresh to confirm.',
          })
        }
      })
    } else if (params.get('upgrade') === 'canceled') {
      window.history.replaceState({}, '', '/settings')
      setFeedback({ type: 'error', msg: 'Upgrade canceled. No charge was made.' })
    }
  }, [setAuth])

  useEffect(() => {
    void Promise.all([
      apiFetch('/api/alerts', AlertsListSchema),
      apiFetch('/api/billing/credits', CreditsSchema),
    ]).then(([alertsR, creditsR]) => {
      if (alertsR.ok) setAlerts(alertsR.data.data)
      if (creditsR.ok) setCredits({ balance: creditsR.data.data.creditsBalance, monthly: creditsR.data.data.monthlyCredits })
      setLoading(false)
    })
  }, [])

  const handleSaveAlert = async () => {
    if (!destination.trim()) return
    setSaving(true)
    setFeedback(null)
    const result = await apiPost('/api/alerts', CreateAlertResponse, {
      type: alertType,
      destination: destination.trim(),
      active: true,
    })
    setSaving(false)
    if (result.ok) {
      setAlerts((prev) => [...prev.filter((a) => a.type !== alertType), result.data.data])
      setDestination('')
      setFeedback({ type: 'success', msg: 'Alert saved.' })
    } else {
      setFeedback({ type: 'error', msg: result.error })
    }
  }

  const handleUpgrade = async () => {
    setBillingLoading(true)
    setFeedback(null)
    const result = await apiPost('/api/billing/checkout', CheckoutUrlSchema, { billingPeriod })
    setBillingLoading(false)
    if (result.ok) {
      window.location.href = result.data.data.url
    } else {
      setFeedback({ type: 'error', msg: result.error })
    }
  }

  const handleManageSubscription = async () => {
    setBillingLoading(true)
    setFeedback(null)
    const result = await apiPost('/api/billing/portal', CheckoutUrlSchema, {})
    setBillingLoading(false)
    if (result.ok) {
      window.location.href = result.data.data.url
    } else {
      setFeedback({ type: 'error', msg: result.error })
    }
  }

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
        <h1 className="text-2xl font-bold text-white">Settings</h1>

        {/* Billing feedback */}
        {feedback && (
          <div
            className={`p-3 rounded-lg text-sm ${
              feedback.type === 'success'
                ? 'bg-secondary/10 text-secondary border border-secondary/30'
                : 'bg-error/10 text-error border border-error/30'
            }`}
          >
            {feedback.msg}
          </div>
        )}

        {/* Tier card */}
        <div id="billing" className="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-outline uppercase tracking-widest mb-2">Current Plan</p>
              <p className="text-lg font-bold text-white capitalize">{user?.tier}</p>
              <p className="text-sm text-on-surface-variant mt-1">{TIER_LABELS[user?.tier ?? 'free']}</p>
              {credits && (
                <p className="text-xs font-mono text-outline mt-2">
                  {credits.balance} / {credits.monthly} credits remaining this month
                </p>
              )}
              {user?.trialExpiresAt && user.tier === 'free' && (
                <p className="text-xs font-mono text-secondary mt-2">
                  Trial ends {new Date(user.trialExpiresAt * 1000).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-3">
              {user?.tier === 'free' && (
                <>
                  <div className="flex rounded-lg border border-outline-variant/20 overflow-hidden text-xs font-mono">
                    <button
                      onClick={() => setBillingPeriod('monthly')}
                      className={`px-3 py-1.5 transition-colors ${
                        billingPeriod === 'monthly'
                          ? 'bg-primary/20 text-primary border-r border-primary/30'
                          : 'text-on-surface-variant hover:bg-surface-container-high border-r border-outline-variant/20'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingPeriod('yearly')}
                      className={`px-3 py-1.5 transition-colors ${
                        billingPeriod === 'yearly'
                          ? 'bg-primary/20 text-primary'
                          : 'text-on-surface-variant hover:bg-surface-container-high'
                      }`}
                    >
                      Yearly <span className="text-secondary ml-1">-20%</span>
                    </button>
                  </div>
                  <button
                    onClick={handleUpgrade}
                    disabled={billingLoading}
                    className="px-5 py-2 rounded-lg border border-primary/30 text-primary text-sm font-medium hover:bg-primary/10 transition-colors disabled:opacity-50"
                  >
                    {billingLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : billingPeriod === 'yearly' ? (
                      'Upgrade to Pro — $144/yr'
                    ) : (
                      'Upgrade to Pro — $14.99/mo'
                    )}
                  </button>
                </>
              )}
              {user?.tier === 'pro' && (
                <button
                  onClick={handleManageSubscription}
                  disabled={billingLoading}
                  className="px-5 py-2 rounded-lg border border-outline-variant/30 text-on-surface-variant text-sm font-medium hover:bg-surface-container-high transition-colors disabled:opacity-50"
                >
                  {billingLoading ? <LoadingSpinner size="sm" /> : 'Manage Subscription'}
                </button>
              )}
              {user?.tier === 'agency' && (
                <a
                  href="mailto:hello@ekorank.com"
                  className="px-5 py-2 rounded-lg border border-outline-variant/30 text-on-surface-variant text-sm font-medium hover:bg-surface-container-high transition-colors"
                >
                  Contact us
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-surface-container rounded-xl border border-outline-variant/10 p-6 space-y-6">
          <h2 className="text-sm font-mono text-outline uppercase tracking-widest">Alerts</h2>

          {loading ? (
            <div className="flex justify-center py-6"><LoadingSpinner /></div>
          ) : (
            <>
              {alerts.length > 0 && (
                <div className="space-y-2">
                  {alerts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-surface-container-low border border-outline-variant/10">
                      <div>
                        <span className="text-xs font-mono text-outline uppercase mr-2">{a.type}</span>
                        <span className="text-sm text-white">{a.destination}</span>
                      </div>
                      <span className={`text-xs font-mono ${a.active ? 'text-secondary' : 'text-outline'}`}>
                        {a.active ? 'ACTIVE' : 'OFF'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-4">
                <div className="flex gap-3">
                  {(['email', 'webhook'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setAlertType(type)}
                      disabled={type === 'webhook' && user?.tier !== 'agency'}
                      className={`flex-1 py-2 rounded-lg border text-sm transition-all ${
                        alertType === type
                          ? 'border-secondary/50 bg-secondary/10 text-secondary'
                          : type === 'webhook' && user?.tier !== 'agency'
                          ? 'border-outline-variant/10 text-outline opacity-50 cursor-not-allowed'
                          : 'border-outline-variant/20 text-on-surface-variant hover:border-outline-variant/40'
                      }`}
                    >
                      {type} {type === 'webhook' && user?.tier !== 'agency' && <span className="text-[8px] text-secondary ml-1">AGENCY</span>}
                    </button>
                  ))}
                </div>
                <input
                  type={alertType === 'email' ? 'email' : 'url'}
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors"
                  placeholder={alertType === 'email' ? 'alert@company.com' : 'https://your-server.com/webhook'}
                />
                <button
                  onClick={handleSaveAlert}
                  disabled={saving || !destination.trim()}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <LoadingSpinner size="sm" /> : 'Save Alert'}
                </button>
              </div>
            </>
          )}
        </div>
    </main>
  )
}
